import { KeyboardEvent, useState, useEffect, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/modal';
import { SaveIcon, Edit, AlertTriangle, Globe, Lock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { DialogClose } from '@/components/ui/dialog';
import { LoadingSpinner } from '@/components/loading-spinner';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useOrgStore } from '@/stores/org-store';
import { useWorkflowStore } from '@/stores/workflow-store';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  TEMPLATE_COLOR_OPTIONS,
  TEMPLATE_ICON_OPTIONS,
  DEFAULT_TEMPLATE_STYLE,
  DEFAULT_TEMPLATE_ICON,
} from '@/constants/template-styling';
import {
  TemplateStyleOption,
  TemplateIconOption,
} from '@/constants/template-styling';
import { getIconComponent } from '../helper/icon-mapping';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import { ROUTES } from '@/constants/routes';
import { DefaultAccessDropdown } from '@/components/default-access-dropdown';
import { useSoftwareRepoStatus } from '@/hooks/queries/use-software-repo-status';
import type { DefaultRoleOption } from '@/types/share-types';
import {
  CLARIFICATION_MODE_OPTIONS,
  CLARIFICATION_TIMEOUT_OPTIONS,
  isClarificationMode,
  snapTimeoutSecs,
  type ClarificationMode,
} from '@/stores/clarification-prefs-store';

interface AssetModeSectionProps {
  value: 'public' | 'private';
  onChange: (value: 'public' | 'private') => void;
}

const AssetModeSection = ({ value, onChange }: AssetModeSectionProps) => {
  const { currentOrg } = useOrgStore();

  const orgDefault = currentOrg?.default_asset_mode ?? 'private';
  const allowOverride = currentOrg?.allow_public_media_override ?? false;
  const isLocked = orgDefault === 'private' && !allowOverride;

  if (isLocked) return null;

  const isPublic = value === 'public';

  const handleToggle = (checked: boolean) => {
    onChange(checked ? 'public' : 'private');
  };

  return (
    <div className='flex items-center justify-between'>
      <div className='flex items-center gap-2'>
        {isPublic ? (
          <Globe className='size-4 text-muted-foreground' />
        ) : (
          <Lock className='size-4 text-muted-foreground' />
        )}
        <div>
          <Label htmlFor='workflow-asset-mode' className='text-sm font-medium'>
            Permanent file URLs
          </Label>
          <p className='text-xs text-muted-foreground'>
            Generated files get public, non-expiring URLs for external
            publishing
          </p>
        </div>
      </div>
      <Switch
        id='workflow-asset-mode'
        checked={isPublic}
        onCheckedChange={handleToggle}
      />
    </div>
  );
};

interface SaveWorkflowModalProps {
  isModalOpen: boolean;
  setIsModalOpen: (isModalOpen: boolean) => void;
  isExistingWorkflow?: boolean;
  currentWorkflowName?: string;
  currentWorkflowDescription?: string;
  workflowId?: string;
  currentIcon?: string;
  currentColor?: string;
  currentBgColor?: string;
  onSave?: (
    name: string,
    description?: string,
    icon?: string,
    color?: string,
    bgColor?: string,
  ) => Promise<void>;
  mode?:
    | 'save'
    | 'editInfo'
    | 'editTemplateInfo'
    | 'template'
    | 'duplicate'
    | 'clone';
  showAssetMode?: boolean;
  // Renders the workflow-level Special Instructions textarea. Only pass
  // from surfaces whose save path persists ``currentWorkflow.metadata``
  // (the canvas editor) -- the list-table edit flow does not.
  showSpecialInstructions?: boolean;
  forceSave?: boolean; // If true, hide cancel button and prevent closing
  currentDefaultRole?: DefaultRoleOption;
  showClientDataWarning?: boolean;
  onDefaultRoleChange?: (role: DefaultRoleOption) => void;
  // Both props must be supplied for the toggle to render, so non-A2A surfaces
  // can reuse this modal without exposing the flag.
  currentPublishToApi?: boolean;
  onPublishToApiChange?: (value: boolean) => void | Promise<void>;
  isWorkflowLocked?: boolean;
}

export const SaveWorkflowModal = ({
  isModalOpen = false,
  setIsModalOpen,
  isExistingWorkflow = false,
  currentWorkflowName = '',
  currentWorkflowDescription = '',
  currentIcon,
  currentColor,
  currentBgColor,
  onSave,
  mode = 'save',
  showAssetMode = false,
  showSpecialInstructions = false,
  forceSave = false,
  currentDefaultRole,
  showClientDataWarning = false,
  onDefaultRoleChange,
  currentPublishToApi,
  onPublishToApiChange,
  isWorkflowLocked = false,
}: SaveWorkflowModalProps) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [assetMode, setAssetMode] = useState<'public' | 'private'>('private');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [clarificationMode, setClarificationMode] =
    useState<ClarificationMode>('auto');
  const [clarificationTimeoutSecs, setClarificationTimeoutSecs] = useState(60);
  // Whether the user interacted with the clarification controls this
  // session. Lets an EXPLICITLY selected "Proceed without asking" persist
  // as a workflow default while an untouched auto stays unset (runs keep
  // following the user's personal preference).
  const [clarificationTouched, setClarificationTouched] = useState(false);
  const [softwareRepoUrl, setSoftwareRepoUrl] = useState('');
  const [softwareRepoBranch, setSoftwareRepoBranch] = useState('');
  const [softwareRepoReadWrite, setSoftwareRepoReadWrite] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [nameError, setNameError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const [specialInstructionsError, setSpecialInstructionsError] = useState('');
  const [softwareRepoUrlError, setSoftwareRepoUrlError] = useState('');
  const softwareRepoTouchedRef = useRef(false);
  const softwareRepoBranchTouchedRef = useRef(false);
  const softwareRepoDefaultsAppliedRef = useRef(false);
  const softwareRepoModalSessionRef = useRef<string | null>(null);
  const [selectedIcon, setSelectedIcon] = useState<TemplateIconOption>(
    DEFAULT_TEMPLATE_ICON,
  );
  const [selectedStyle, setSelectedStyle] = useState<TemplateStyleOption>(
    DEFAULT_TEMPLATE_STYLE,
  );
  const navigate = useOrgNavigate();
  const isTemplateMode = mode === 'template' || mode === 'editTemplateInfo';
  const { currentOrg } = useOrgStore();
  const { currentWorkflow, setCurrentWorkflow } = useWorkflowStore();
  // Software repository settings ride the workflow save payload the same
  // way asset_mode / special_instructions do, so this section only
  // renders on surfaces whose save path persists ``currentWorkflow``
  // (the canvas editor) — and only when the org has a repository
  // provider configured. While the status is loading or errored the
  // section stays hidden and any saved values are left untouched on
  // save, so nothing gets blanked just because the section isn't shown.
  const canShowSoftwareRepo = showSpecialInstructions && !isTemplateMode;
  const { data: softwareRepoStatus } =
    useSoftwareRepoStatus(canShowSoftwareRepo);
  const showSoftwareRepo =
    canShowSoftwareRepo && softwareRepoStatus?.configured === true;
  const hasSoftwareRepoUrl = softwareRepoUrl.trim().length > 0;
  const workflowHasStoredRepoFields =
    !!currentWorkflow &&
    (Object.prototype.hasOwnProperty.call(
      currentWorkflow,
      'software_repo_url',
    ) ||
      Object.prototype.hasOwnProperty.call(
        currentWorkflow,
        'software_repo_branch',
      ));
  const isFreshBlankWorkflow =
    mode === 'save' &&
    !isExistingWorkflow &&
    currentWorkflow?.id === 'new' &&
    !workflowHasStoredRepoFields;

  // Initialize asset mode from workflow store when modal opens
  useEffect(() => {
    if (isModalOpen) {
      const orgDefault = currentOrg?.default_asset_mode ?? 'private';
      setAssetMode(currentWorkflow?.metadata?.asset_mode ?? orgDefault);
    }
  }, [
    isModalOpen,
    currentOrg?.default_asset_mode,
    currentWorkflow?.metadata?.asset_mode,
  ]);

  // Initialize special instructions from workflow store when modal opens
  useEffect(() => {
    if (isModalOpen) {
      setSpecialInstructions(
        currentWorkflow?.metadata?.special_instructions ?? '',
      );
    }
  }, [isModalOpen, currentWorkflow?.metadata?.special_instructions]);

  // Initialize each modal session from the workflow exactly once. Property
  // presence matters: ``undefined``/absent on a genuinely blank workflow can
  // inherit the org default, while an explicit null from a persisted workflow
  // or template means disabled and must stay blank.
  useEffect(() => {
    if (!isModalOpen) {
      softwareRepoModalSessionRef.current = null;
      return;
    }

    const storedRepoState = workflowHasStoredRepoFields
      ? `${currentWorkflow?.software_repo_url ?? 'null'}:${currentWorkflow?.software_repo_branch ?? 'null'}:${currentWorkflow?.software_repo_mode ?? 'null'}`
      : 'absent';
    const sessionKey = `${currentWorkflow?.id ?? 'none'}:${mode}:${String(isExistingWorkflow)}:${storedRepoState}`;
    if (softwareRepoModalSessionRef.current === sessionKey) return;

    softwareRepoModalSessionRef.current = sessionKey;
    softwareRepoTouchedRef.current = false;
    softwareRepoBranchTouchedRef.current = false;
    softwareRepoDefaultsAppliedRef.current = false;
    setSoftwareRepoUrl(currentWorkflow?.software_repo_url ?? '');
    setSoftwareRepoBranch(currentWorkflow?.software_repo_branch ?? '');
    setSoftwareRepoReadWrite(
      currentWorkflow?.software_repo_mode === 'read_write',
    );
    setSoftwareRepoUrlError('');
  }, [
    isModalOpen,
    mode,
    isExistingWorkflow,
    currentWorkflow?.id,
    currentWorkflow?.software_repo_url,
    currentWorkflow?.software_repo_branch,
    currentWorkflow?.software_repo_mode,
    workflowHasStoredRepoFields,
  ]);

  // Apply asynchronously loaded defaults only to a genuinely new blank
  // workflow. A user edit (including clearing a field) wins even when it
  // happens before this query resolves.
  useEffect(() => {
    const defaultRepoUrl = softwareRepoStatus?.default_repo_url?.trim();
    if (
      !isModalOpen ||
      !showSoftwareRepo ||
      !isFreshBlankWorkflow ||
      !defaultRepoUrl ||
      softwareRepoTouchedRef.current ||
      softwareRepoDefaultsAppliedRef.current
    ) {
      return;
    }

    setSoftwareRepoUrl(defaultRepoUrl);
    setSoftwareRepoBranch(softwareRepoStatus?.default_branch?.trim() || '');
    softwareRepoDefaultsAppliedRef.current = true;
  }, [
    isModalOpen,
    showSoftwareRepo,
    isFreshBlankWorkflow,
    softwareRepoStatus?.default_repo_url,
    softwareRepoStatus?.default_branch,
  ]);

  // Whether the workflow already carries a clarification default. Drives
  // the persist decision: an untouched "Proceed without asking" on a
  // workflow that never had a default stays UNSET (so runs keep following
  // the user's personal preference), while an explicit change — or any
  // previously-set value — persists.
  const storedClarificationMode = useMemo(() => {
    const raw = currentWorkflow?.metadata?.default_clarification_mode;
    return isClarificationMode(raw) ? raw : undefined;
  }, [currentWorkflow?.metadata?.default_clarification_mode]);

  // Initialize the clarification default controls when the modal opens.
  useEffect(() => {
    if (isModalOpen) {
      setClarificationTouched(false);
      setClarificationMode(storedClarificationMode ?? 'auto');
      const storedTimeout =
        currentWorkflow?.metadata?.default_clarification_timeout_secs;
      // Snap off-grid metadata values to an allowed option — Radix Select
      // renders blank when ``value`` matches no ``SelectItem``.
      setClarificationTimeoutSecs(
        typeof storedTimeout === 'number' && storedTimeout > 0
          ? snapTimeoutSecs(storedTimeout)
          : 60,
      );
    }
  }, [
    isModalOpen,
    storedClarificationMode,
    currentWorkflow?.metadata?.default_clarification_timeout_secs,
  ]);

  // Initialize form with existing workflow data when editing
  useEffect(() => {
    if (isExistingWorkflow && isModalOpen) {
      setName(currentWorkflowName);
      setDescription(currentWorkflowDescription);

      // Initialize styling fields for template editing
      if (isTemplateMode || (currentIcon && currentColor && currentBgColor)) {
        const existingIcon =
          TEMPLATE_ICON_OPTIONS.find((i) => i.icon === currentIcon) ||
          DEFAULT_TEMPLATE_ICON;
        const existingStyle =
          TEMPLATE_COLOR_OPTIONS.find(
            (s) =>
              s.color === currentColor && s.backgroundColor === currentBgColor,
          ) || DEFAULT_TEMPLATE_STYLE;
        setSelectedIcon(existingIcon);
        setSelectedStyle(existingStyle);
      }
    } else if ((mode === 'duplicate' || mode === 'clone') && isModalOpen) {
      setName(currentWorkflowName || 'Untitled');
      setDescription(currentWorkflowDescription);
    } else if (
      !isExistingWorkflow &&
      mode !== 'duplicate' &&
      mode !== 'clone' &&
      isModalOpen
    ) {
      // Use the name passed from parent - don't generate a new one here
      // Name generation happens in loadTemplateWorkflow/createBlankWorkflow
      setName(currentWorkflowName || '');
      setDescription('');
      // Reset styling fields for new templates
      if (mode === 'template') {
        setSelectedIcon(DEFAULT_TEMPLATE_ICON);
        setSelectedStyle(DEFAULT_TEMPLATE_STYLE);
      }
    }
  }, [
    isExistingWorkflow,
    mode,
    isModalOpen,
    currentWorkflowName,
    currentWorkflowDescription,
    currentIcon,
    currentColor,
    currentBgColor,
    forceSave,
    isTemplateMode,
  ]);

  const handleSaveWorkflow = async (
    name: string,
    description: string = '',
    icon: string = '',
    color: string = '',
    bgColor: string = '',
  ) => {
    const trimmedName = name.trim();
    const trimmedDescription = description?.trim();
    const entityType = isTemplateMode ? 'Template' : 'Workflow';

    if (!trimmedName) {
      setNameError(`${entityType} name is required`);
      setDescriptionError('');
      return;
    }

    // 255 is the real bound: the backend workflow.name column is
    // VARCHAR(255). Any character is allowed — names never enter URLs
    // (workflows are UUID-addressed), and consumers that need a safe form
    // (file exports, A2A alias slugs) sanitize at their own boundary.
    if (trimmedName.length > 255) {
      setNameError(`${entityType} name cannot exceed 255 characters`);
      setDescriptionError('');
      return;
    }

    // The description column is unbounded TEXT; 5000 is a generous
    // guardrail against accidental full-document pastes, not a schema
    // limit.
    if (trimmedDescription && trimmedDescription.length > 5000) {
      setNameError('');
      setDescriptionError(
        `${entityType} description cannot exceed 5000 characters`,
      );
      return;
    }

    const trimmedSpecialInstructions = specialInstructions.trim();
    if (showSpecialInstructions && trimmedSpecialInstructions.length > 4000) {
      setNameError('');
      setDescriptionError('');
      setSpecialInstructionsError(
        'Special instructions cannot exceed 4000 characters',
      );
      return;
    }

    const trimmedRepoUrl = softwareRepoUrl.trim();
    if (
      showSoftwareRepo &&
      trimmedRepoUrl &&
      !trimmedRepoUrl.startsWith('https://')
    ) {
      setNameError('');
      setDescriptionError('');
      setSpecialInstructionsError('');
      setSoftwareRepoUrlError('Repository URL must start with https://');
      return;
    }

    // Clear all errors if validation passes
    setNameError('');
    setDescriptionError('');
    setSpecialInstructionsError('');
    setSoftwareRepoUrlError('');

    setIsLoading(true);

    try {
      if (onSave) {
        // Persist the clarification default only when it carries signal:
        // a non-auto mode, auto on a workflow that previously had a
        // default, or an auto the user explicitly selected this session.
        // Leaving an UNTOUCHED auto UNSET preserves the pre-feature
        // behaviour where runs follow the user's personal preference.
        const persistedClarificationMode =
          clarificationMode !== 'auto' ||
          storedClarificationMode ||
          clarificationTouched
            ? clarificationMode
            : undefined;
        const persistedClarificationTimeout =
          persistedClarificationMode === 'ask_with_timeout'
            ? clarificationTimeoutSecs
            : undefined;

        const persistedRepoUrl = trimmedRepoUrl || null;
        const persistedRepoBranch = persistedRepoUrl
          ? softwareRepoBranch.trim() || null
          : null;
        const selectedRepoMode = softwareRepoReadWrite ? 'read_write' : 'read';
        const persistedRepoMode = persistedRepoUrl ? selectedRepoMode : null;

        // Commit asset mode, special instructions, the clarification
        // default, and software repository settings to store before save
        // so the payload includes them
        if (currentWorkflow) {
          setCurrentWorkflow({
            ...currentWorkflow,
            ...(showSoftwareRepo && {
              software_repo_url: persistedRepoUrl,
              software_repo_branch: persistedRepoBranch,
              software_repo_mode: persistedRepoMode,
            }),
            metadata: {
              ...(currentWorkflow.metadata ?? {}),
              asset_mode: assetMode,
              ...(showSpecialInstructions && {
                special_instructions: trimmedSpecialInstructions || undefined,
                default_clarification_mode: persistedClarificationMode,
                default_clarification_timeout_secs:
                  persistedClarificationTimeout,
              }),
            },
          });
        }

        await onSave(trimmedName, trimmedDescription, icon, color, bgColor);

        // Reset form and close modal on success
        // Success notifications are handled by the calling function
        setName('');
        setDescription('');
        setSpecialInstructions('');
        setSoftwareRepoUrl('');
        setSoftwareRepoBranch('');
        setSoftwareRepoReadWrite(false);
        softwareRepoTouchedRef.current = false;
        softwareRepoBranchTouchedRef.current = false;
        softwareRepoDefaultsAppliedRef.current = false;
        softwareRepoModalSessionRef.current = null;
        setSelectedIcon(DEFAULT_TEMPLATE_ICON);
        setSelectedStyle(DEFAULT_TEMPLATE_STYLE);
        setIsModalOpen(false);
      }
    } catch (error) {
      console.error('Save workflow error:', error);
      throw error; // Re-throw to let the calling function handle the error
    } finally {
      setIsLoading(false);
    }
  };

  const getStylingParams = () => {
    const shouldIncludeStyling =
      isTemplateMode ||
      (isExistingWorkflow && (currentIcon || currentColor || currentBgColor));

    return shouldIncludeStyling
      ? {
          icon: selectedIcon.icon,
          color: selectedStyle.color,
          bgColor: selectedStyle.backgroundColor,
        }
      : {
          icon: undefined,
          color: undefined,
          bgColor: undefined,
        };
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && name?.trim()) {
      const { icon, color, bgColor } = getStylingParams();
      handleSaveWorkflow(name, description, icon, color, bgColor);
    }
  };

  const Title = () => {
    const getTitle = () => {
      // New workflows (not existing) should also show "Create Workflow"
      if (mode === 'save' && !isExistingWorkflow) {
        return 'Create Workflow';
      }
      switch (mode) {
        case 'editInfo':
          return 'Edit Workflow Attributes';
        case 'editTemplateInfo':
          return 'Edit Template Attributes';
        case 'template':
          return 'Save as Template';
        case 'duplicate':
          return 'Duplicate Workflow';
        case 'clone':
          return 'Clone Workflow';
        case 'save':
        default:
          return isExistingWorkflow ? 'Edit workflow' : 'Save workflow';
      }
    };

    const getIcon = () => {
      switch (mode) {
        case 'editInfo':
        case 'editTemplateInfo':
          return <Edit className='size-5' />;
        case 'template':
        case 'duplicate':
        case 'clone':
          return <SaveIcon className='size-5' />;
        case 'save':
        default:
          return isExistingWorkflow ? (
            <Edit className='size-5' />
          ) : (
            <SaveIcon className='size-5' />
          );
      }
    };

    return (
      <div className='flex items-center gap-2'>
        {getIcon()}
        {getTitle()}
      </div>
    );
  };

  const Footer = () => {
    const getButtonText = () => {
      switch (mode) {
        case 'editInfo':
        case 'editTemplateInfo':
          return isLoading ? 'Saving...' : 'Save';
        case 'template':
          return isLoading ? 'Saving...' : 'Save as Template';
        case 'duplicate':
          return isLoading ? 'Duplicating...' : 'Duplicate';
        case 'clone':
          return isLoading ? 'Cloning...' : 'Clone';
        case 'save':
        default:
          return isLoading
            ? isExistingWorkflow
              ? 'Saving...'
              : 'Creating...'
            : isExistingWorkflow
              ? 'Save'
              : 'Create';
      }
    };

    return (
      <>
        <DialogClose asChild>
          <Button
            variant='outline'
            onClick={() => {
              if (mode === 'save' && !isExistingWorkflow) {
                // if this is a new workflow creation flow, cancelling will redirect to workflows page
                navigate(ROUTES.WORKFLOW.ROOT);
              }
            }}
          >
            Cancel
          </Button>
        </DialogClose>

        <Button
          disabled={!name?.trim() || isLoading}
          onClick={() => {
            const { icon, color, bgColor } = getStylingParams();
            handleSaveWorkflow(name, description, icon, color, bgColor);
          }}
        >
          {isLoading ? (
            <>
              <LoadingSpinner /> {getButtonText()}
            </>
          ) : (
            getButtonText()
          )}
        </Button>
      </>
    );
  };

  return (
    <Modal
      isDialogOpen={isModalOpen}
      setIsDialogOpen={(isModalOpen) => {
        setName('');
        setDescription('');
        setSpecialInstructions('');
        setSoftwareRepoUrl('');
        setSoftwareRepoBranch('');
        setSoftwareRepoReadWrite(false);
        softwareRepoTouchedRef.current = false;
        softwareRepoBranchTouchedRef.current = false;
        softwareRepoDefaultsAppliedRef.current = false;
        softwareRepoModalSessionRef.current = null;
        setNameError('');
        setDescriptionError('');
        setSpecialInstructionsError('');
        setSoftwareRepoUrlError('');
        setSelectedIcon(DEFAULT_TEMPLATE_ICON);
        setSelectedStyle(DEFAULT_TEMPLATE_STYLE);
        setIsModalOpen(isModalOpen);
      }}
      title={<Title />}
      footer={<Footer />}
      hideCloseButton={forceSave || (mode === 'save' && !isExistingWorkflow)}
      preventClose={forceSave}
    >
      <div className='mt-4 space-y-4'>
        <div>
          <Label
            htmlFor='name'
            className='label-required block mb-2 text-sm font-medium'
          >
            {isTemplateMode ? 'Template Name' : 'Workflow Name'}
          </Label>
          <Input
            id='name'
            error={nameError}
            onChange={(event) => {
              setNameError('');
              setName(event.target.value);
            }}
            onKeyDown={handleKeyDown}
            value={name}
            placeholder={
              isTemplateMode ? 'Enter template name' : 'Enter workflow name'
            }
            autoComplete='off'
          />
        </div>

        <div>
          <Label
            htmlFor='description'
            className='block mb-2 text-sm font-medium'
          >
            {isTemplateMode ? 'Template Description' : 'Description'}
          </Label>
          <div className='flex w-full flex-col gap-1'>
            <Textarea
              id='description'
              onChange={(event) => {
                setDescriptionError('');
                setDescription(event.target.value);
              }}
              value={description}
              placeholder={
                isTemplateMode
                  ? 'Enter template description'
                  : 'Enter workflow description'
              }
              rows={3}
              className={
                descriptionError
                  ? 'border-rose-600 dark:border-rose-400 resize-none'
                  : 'resize-none'
              }
            />
            {descriptionError && (
              <span className='text-xs text-rose-600 pl-[13px] dark:text-rose-400 break-words'>
                {descriptionError}
              </span>
            )}
          </div>
        </div>

        {mode === 'editInfo' && onDefaultRoleChange && (
          <div className='flex flex-col gap-2'>
            <div className='flex items-center justify-between'>
              <div>
                <Label className='block text-sm font-medium'>
                  Default Access
                </Label>
                <p className='text-xs text-muted-foreground'>
                  Access level for all org members
                </p>
              </div>
              <DefaultAccessDropdown
                value={currentDefaultRole ?? 'workflow:viewer'}
                onChange={onDefaultRoleChange}
                showClientDataWarning={showClientDataWarning}
                showWarningBanner={false}
              />
            </div>
            {showClientDataWarning &&
              currentDefaultRole === 'workflow:editor' && (
                <div className='flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-200'>
                  <AlertTriangle className='size-4 shrink-0 mt-0.5' />
                  <span>
                    This workflow is accessible to all organization members.
                    Ensure it does not contain client-specific or sensitive
                    data.
                  </span>
                </div>
              )}
          </div>
        )}

        {/* Persists immediately so the A2A gateway starts surfacing this
            workflow to allowlisted agents. Locked workflows are read-only. */}
        {mode === 'editInfo' &&
          onPublishToApiChange &&
          currentPublishToApi !== undefined && (
            <div className='flex items-center justify-between'>
              <div>
                <Label className='block text-sm font-medium'>
                  Expose via A2A API
                </Label>
                <p className='text-xs text-muted-foreground'>
                  Allowlisted agents can call this workflow via
                  <code className='mx-1 rounded bg-muted px-1 py-0.5 text-[10px]'>
                    list_workflows
                  </code>
                  /
                  <code className='mx-1 rounded bg-muted px-1 py-0.5 text-[10px]'>
                    call_workflow
                  </code>
                  .
                </p>
              </div>
              <Switch
                checked={Boolean(currentPublishToApi)}
                disabled={isWorkflowLocked}
                onCheckedChange={(next) => {
                  // Caller handles the optimistic flip + rollback +
                  // persistence; we just forward the new value.
                  void onPublishToApiChange(next);
                }}
                aria-label='Expose this workflow via the A2A protocol'
              />
            </div>
          )}

        {showSpecialInstructions && !isTemplateMode && (
          <div>
            <Label
              htmlFor='special-instructions'
              className='block mb-2 text-sm font-medium'
            >
              Special Instructions
            </Label>
            <div className='flex w-full flex-col gap-1'>
              <Textarea
                id='special-instructions'
                onChange={(event) => {
                  setSpecialInstructionsError('');
                  setSpecialInstructions(event.target.value);
                }}
                value={specialInstructions}
                placeholder='e.g. Always respond in formal English and cite all sources'
                rows={4}
                className={
                  specialInstructionsError
                    ? 'border-rose-600 dark:border-rose-400 resize-none'
                    : 'resize-none'
                }
              />
              <p className='text-xs text-muted-foreground'>
                Provided as context to every agent in this workflow when it
                runs.
              </p>
              {specialInstructionsError && (
                <span className='text-xs text-rose-600 pl-[13px] dark:text-rose-400 break-words'>
                  {specialInstructionsError}
                </span>
              )}
            </div>
          </div>
        )}

        {showSpecialInstructions && !isTemplateMode && (
          <div>
            <Label
              htmlFor='clarification-default-mode'
              className='block mb-2 text-sm font-medium'
            >
              Clarifying questions
            </Label>
            <div className='flex items-center gap-3'>
              <Select
                value={clarificationMode}
                onValueChange={(value) => {
                  if (isClarificationMode(value)) {
                    setClarificationTouched(true);
                    setClarificationMode(value);
                  }
                }}
              >
                <SelectTrigger
                  id='clarification-default-mode'
                  className='flex-1'
                >
                  <SelectValue placeholder='Select a default' />
                </SelectTrigger>
                <SelectContent>
                  {CLARIFICATION_MODE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {clarificationMode === 'ask_with_timeout' && (
                <Select
                  value={String(clarificationTimeoutSecs)}
                  onValueChange={(value) =>
                    setClarificationTimeoutSecs(Number(value))
                  }
                >
                  <SelectTrigger
                    id='clarification-default-timeout'
                    className='w-36'
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CLARIFICATION_TIMEOUT_OPTIONS.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={String(option.value)}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <p className='mt-1 text-xs text-muted-foreground'>
              Default for how agents handle clarifying questions mid run. Runs
              started without explicit clarification settings use this default.
            </p>
          </div>
        )}

        {showSoftwareRepo && (
          <div>
            <Label
              htmlFor='software-repo-url'
              className='block mb-2 text-sm font-medium'
            >
              Software repository
            </Label>
            <div className='flex flex-col gap-3'>
              <div className='flex w-full flex-col gap-1'>
                <Input
                  id='software-repo-url'
                  error={softwareRepoUrlError}
                  value={softwareRepoUrl}
                  onChange={(event) => {
                    const nextRepoUrl = event.target.value;
                    const wasEmpty = !softwareRepoUrl.trim();
                    softwareRepoTouchedRef.current = true;
                    setSoftwareRepoUrlError('');
                    setSoftwareRepoUrl(nextRepoUrl);
                    if (
                      wasEmpty &&
                      nextRepoUrl.trim() &&
                      !softwareRepoBranch.trim() &&
                      !softwareRepoBranchTouchedRef.current &&
                      softwareRepoStatus?.default_branch
                    ) {
                      setSoftwareRepoBranch(softwareRepoStatus.default_branch);
                    }
                  }}
                  placeholder='https://github.com/org/repo.git'
                  autoComplete='off'
                />
                <p className='text-xs text-muted-foreground'>
                  {softwareRepoStatus?.default_repo_full_name ? (
                    <>
                      Organization default:{' '}
                      {softwareRepoStatus.default_repo_full_name}
                      {softwareRepoStatus.default_branch
                        ? ` @ ${softwareRepoStatus.default_branch}`
                        : ''}
                      . Changes here affect only this workflow; leave the
                      repository empty to disable it.
                    </>
                  ) : (
                    <>
                      Organization defaults prefill new workflows only. Changes
                      here affect only this workflow; leave the repository empty
                      to disable it.
                    </>
                  )}
                </p>
              </div>
              <div>
                <Label
                  htmlFor='software-repo-branch'
                  className='block mb-1 text-xs text-muted-foreground'
                >
                  Branch
                </Label>
                <Input
                  id='software-repo-branch'
                  value={softwareRepoBranch}
                  onChange={(event) => {
                    softwareRepoTouchedRef.current = true;
                    softwareRepoBranchTouchedRef.current = true;
                    setSoftwareRepoBranch(event.target.value);
                  }}
                  placeholder='main'
                  autoComplete='off'
                  disabled={!hasSoftwareRepoUrl}
                />
              </div>
              <div className='flex items-center justify-between gap-4'>
                <div>
                  <Label
                    htmlFor='software-repo-mode'
                    className='text-sm font-medium'
                  >
                    Allow agents to commit code (read/write)
                  </Label>
                  <p className='text-xs text-muted-foreground'>
                    When off, agents only reuse existing code. When on, agents
                    may commit improvements back — and only runs started by the
                    workflow owner can ever write.
                  </p>
                </div>
                <Switch
                  id='software-repo-mode'
                  checked={softwareRepoReadWrite}
                  onCheckedChange={setSoftwareRepoReadWrite}
                  disabled={!hasSoftwareRepoUrl}
                  aria-label='Allow agents to commit code to the software repository'
                />
              </div>
            </div>
          </div>
        )}

        {(mode === 'save' || showAssetMode) && (
          <AssetModeSection value={assetMode} onChange={setAssetMode} />
        )}

        {(isTemplateMode ||
          (isExistingWorkflow &&
            (currentIcon || currentColor || currentBgColor))) && (
          <>
            {/* Template Styling - All on one row */}
            <div>
              <Label className='label-required block mb-2 text-sm font-medium dark:text-white'>
                Template Styling
              </Label>
              <div className='flex items-center gap-3'>
                {/* Icon Selection */}
                <div className='flex-1'>
                  <Label className='block mb-1 text-xs text-gray-600 dark:text-white'>
                    Icon
                  </Label>
                  <Select
                    value={selectedIcon.icon}
                    onValueChange={(value) => {
                      const icon = TEMPLATE_ICON_OPTIONS.find(
                        (i) => i.icon === value,
                      );
                      if (icon) setSelectedIcon(icon);
                    }}
                  >
                    <SelectTrigger className='w-full'>
                      <SelectValue placeholder='Select an icon' />
                    </SelectTrigger>
                    <SelectContent>
                      {TEMPLATE_ICON_OPTIONS.map((icon) => (
                        <SelectItem key={icon.icon} value={icon.icon}>
                          <div className='flex items-center gap-2'>
                            <span className='text-sm'>{icon.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Color Selection */}
                <div className='flex-1'>
                  <Label className='block mb-1 text-xs text-gray-600 dark:text-white'>
                    Color
                  </Label>
                  <Select
                    value={selectedStyle.name}
                    onValueChange={(value) => {
                      const style = TEMPLATE_COLOR_OPTIONS.find(
                        (s) => s.name === value,
                      );
                      if (style) setSelectedStyle(style);
                    }}
                  >
                    <SelectTrigger className='w-full'>
                      <SelectValue placeholder='Select a color' />
                    </SelectTrigger>
                    <SelectContent>
                      {TEMPLATE_COLOR_OPTIONS.map((style) => (
                        <SelectItem key={style.name} value={style.name}>
                          <div className='flex items-center gap-2'>
                            <div
                              className='w-4 h-4 rounded border'
                              style={{ backgroundColor: style.backgroundColor }}
                            />
                            <span className='text-sm'>{style.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Preview */}
                <div className='shrink-0'>
                  <Label className='block mb-1 text-xs text-gray-600 dark:text-white'>
                    Preview
                  </Label>
                  <div
                    className='w-12 h-12 rounded-lg flex items-center justify-center'
                    style={{
                      backgroundColor: selectedStyle.backgroundColor,
                    }}
                  >
                    {getIconComponent(
                      selectedIcon.icon,
                      selectedStyle.color,
                      'w-6 h-6',
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
